import React, { useState, useContext, useEffect } from 'react';
import ImageDataContext from './imageData-context';
import LoadingContext from '../components/layout/search/loading-context';
import ImageService from '../services/ImageService';
import ImageListService from '../services/ImageListService';
import ActivityHistoryService from '../services/ActivityHistoryService';
import { documentDefinition } from '../types/definitions/Document';
import { useOktaAuth } from '@okta/okta-react';
import { imageUrlResponse } from '../types/response/ImageUrlResponse';
import { activityHistoryEntry } from '../types/definitions/ActivityHistoryEntry';
import Common from '../common/Common';
import useUserActivity from '../hooks/useUserActivity';

interface Props {
    children: React.ReactNode;
}

const ImageDataProvider = (props: Props) => {
    const [imagesListData, setImagesListData] = useState([] as documentDefinition[]);
    const [activityHistoryData, setActivityHistoryData] = useState([] as activityHistoryEntry[]);
    const [loadedImageData, setLoadedImageData] = useState({
        loadedImageDetails: {} as documentDefinition,
        loadedImagePageNumber: 1,
        loadedImagePageUrl: '',
        loadedImageFromImageList: false,
        loadedImagePagesReady: 0,
        loadedImageTokenExpires: new Date(Date.now()) as Date,
    });
    const { oktaAuth, authState } = useOktaAuth();
    const loadingCtx = useContext(LoadingContext);
    const [cachedImageDetails, setCachedImageDetails] = useState([] as documentDefinition[]);
    const [taggedPages, setTaggedPages] = useState(new Set());
    const [isUserActive, resetUserActivity] = useUserActivity(3600, true);

    const userName: string = authState?.accessToken?.claims?.sub;
    const cid: string = authState?.accessToken?.claims?.cid;
    const accessToken: string = authState?.accessToken?.accessToken;

    useEffect(() => {
        if (!isUserActive) {
            logout();
        }
    }, [isUserActive]);

    const logout = async () => {
        await oktaAuth.revokeAccessToken(); // explicitly revoke token
        await oktaAuth.revokeRefreshToken(); // explicitly refresh token
        // Will redirect to Okta to end the session then redirect back to the configured `postLogoutRedirectUri`
        await oktaAuth.signOut();
        oktaAuth.tokenManager.clear();
    };

    useEffect(() => {
        if (!userName) {
            return;
        }

        loadImageList();
        loadActivityHistory();
    }, [authState]);

    const loadImageList = () => {
        loadingCtx.setIsLoading(true, 'ImagesList');
        ImageListService.getImageList(userName, accessToken, cid)
            .then((response) => {
                const castedResponse: documentDefinition[] = response.data;

                const sortedImageList = castedResponse.sort((a, b) => {
                    const aDate = new Date(a.fileAccessDate).getTime();
                    const bDate = new Date(b.fileAccessDate).getTime();
                    return aDate < bDate ? 1 : -1;
                });

                setImagesListData(sortedImageList);
                loadingCtx.setIsLoading(false, 'ImagesList');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ImagesList');
            });
    };

    const loadActivityHistory = () => {
        loadingCtx.setIsLoading(true, 'ActivityHistory2');
        ActivityHistoryService.getActivityHistoryList(userName, accessToken, cid)
            .then((response) => {
                const castedResponse: activityHistoryEntry[] = response.data;

                const sortedActivityHistory = castedResponse.sort((a, b) => {
                    const aDate = new Date(a.fileAccessDate).getTime();
                    const bDate = new Date(b.fileAccessDate).getTime();
                    return aDate < bDate ? 1 : -1;
                });

                setActivityHistoryData(sortedActivityHistory);
                loadingCtx.setIsLoading(false, 'ActivityHistory2');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ActivityHistory2');
            });
    };

    const expirationInMinutes = 15;

    const addTaggedPageHandler = (page: number) => {
        setTaggedPages((prevState) => {
            let newSet = new Set(prevState);
            newSet.add(page);
            return newSet;
        });
    };
    const removeTaggedPageHandler = (page: number) => {
        setTaggedPages((prevState) => {
            let newSet = new Set(prevState);
            newSet.delete(page);
            return newSet;
        });
    };
    const clearTaggedPagesHandler = () => {
        setTaggedPages(new Set());
    };

    const setLoadedImageHandler = async (fileName: string, fromImageList: boolean) => {
        // set loading true
        loadingCtx.setIsLoading(true, 'ImageDataProvider setLoadedImageHandler');

        const pageToLoad = 1;

        // If a new image is being loaded we clear the tagged pages collection.
        setTaggedPages(new Set());

        // Go to Server for file details
        let fileDetailsResponse = undefined;
        await ImageService.getFileDetails(fileName, accessToken, cid)
            .then((response) => {
                fileDetailsResponse = response.data as documentDefinition;
            })
            .catch((ex) => {
                loadingCtx.setIsLoading(false, 'ImageDataProvider setLoadedImageHandler');
                return;
            });
        if (!fileDetailsResponse) {
            //error
            return;
        }
        const fileDetails: documentDefinition = fileDetailsResponse;

        if (fromImageList) {
            // If this is from the Image List, we want to update Activity History
            const fileDetailsWithAccessDate = ImageService.addUniqueAccessDates([fileDetails]);
            await addImagesToActivityHistoryHandler(fileDetailsWithAccessDate);
        }

        let imageUrlResponse = undefined;
        await ImageService.getImageUrl(fileDetails.name, 1, accessToken, cid)
            .then((response) => {
                imageUrlResponse = response.data;
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ImageDataProvider setLoadedImageHandler');
                return;
            });
        if (!imageUrlResponse) {
            // error
            return;
        }
        const imageUrl: imageUrlResponse = imageUrlResponse;

        setLoadedImageData({
            loadedImageDetails: fileDetails,
            loadedImagePageNumber: imageUrl.pageNumber,
            loadedImagePageUrl: imageUrl.url,
            loadedImageFromImageList: fromImageList,
            loadedImagePagesReady: 0,
            loadedImageTokenExpires: new Date(Date.now() + expirationInMinutes * 60000),
        });

        loadingCtx.setIsLoading(false, 'ImageDataProvider setLoadedImageHandler');
    };

    const clearLoadedImageHandler = () => {
        setLoadedImageData({
            loadedImageDetails: {} as documentDefinition,
            loadedImagePageNumber: 1,
            loadedImagePageUrl: '',
            loadedImageFromImageList: false,
            loadedImagePagesReady: 0,
            loadedImageTokenExpires: new Date(Date.now()),
        });
    };

    const setLoadedImagePageNumberHandler = (pageNumber: number) => {
        loadingCtx.setIsLoading(true, 'ImageDataProvider setLoadedImagePageNumberHandler');

        const now = new Date(Date.now());
        if (loadedImageData.loadedImageTokenExpires < now) {
            ImageService.getImageUrl(
                loadedImageData.loadedImageDetails.name,
                pageNumber,
                accessToken,
                cid
            )
                .then((response) => {
                    const responseData: imageUrlResponse = response.data;

                    setLoadedImageData((prevState) => {
                        return {
                            ...prevState,
                            loadedImagePageNumber: responseData.pageNumber,
                            loadedImagePageUrl: responseData.url,
                            loadedImageTokenExpires: new Date(
                                Date.now() + expirationInMinutes * 60000
                            ),
                        };
                    });
                    loadingCtx.setIsLoading(
                        false,
                        'ImageDataProvider setLoadedImagePageNumberHandler'
                    );
                })
                .catch((ex) => {
                    //setError(ex.response);
                    //setIsError(true);
                    loadingCtx.setIsLoading(
                        false,
                        'ImageDataProvider setLoadedImagePageNumberHandler'
                    );
                });
        } else {
            ImageService.getImageView(
                loadedImageData.loadedImageDetails.name,
                pageNumber,
                accessToken,
                cid
            );
            setLoadedImageData((prevState) => {
                const isHierarchy = prevState.loadedImageDetails.name.startsWith('HIERARCHY_');
                const oldPageNumber = prevState.loadedImagePageNumber;
                const replaceString = isHierarchy
                    ? `pageNumber=${oldPageNumber}&`
                    : '/' + oldPageNumber.toString() + '.';
                const replacementString = isHierarchy
                    ? `pageNumber=${pageNumber}&`
                    : '/' + pageNumber.toString() + '.';
                const newUrl = prevState.loadedImagePageUrl.replace(
                    replaceString,
                    replacementString
                );
                return {
                    ...prevState,
                    loadedImagePageNumber: pageNumber,
                    loadedImagePageUrl: newUrl,
                };
            });
            loadingCtx.setIsLoading(false, 'ImageDataProvider setLoadedImagePageNumberHandler');
        }
    };

    const addImagesToImagesListHandler = async (imagesToAdd: documentDefinition[]) => {
        loadingCtx.setIsLoading(true, 'ImagesList addImagesToImagesListHandler');
        await ImageListService.postImageList(imagesToAdd, userName, accessToken)
            .then((response) => {
                loadingCtx.setIsLoading(false, 'ImagesList addImagesToImagesListHandler');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ImagesList addImagesToImagesListHandler');
            });

        loadImageList();

        setLoadedImageHandler(imagesToAdd[0].name, true);
    };

    const removeImagesFromImagesListHandler = (imagesToRemove: documentDefinition[]) => {
        const imageNamesToRemove: string[] = imagesToRemove.map((i) => i.name);

        loadingCtx.setIsLoading(true, 'ImagesList removeImagesFromImagesListHandler');
        ImageListService.deleteImageList(imageNamesToRemove, userName, accessToken, cid)
            .then((response) => {
                loadingCtx.setIsLoading(false, 'ImagesList removeImagesFromImagesListHandler');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ImagesList removeImagesFromImagesListHandler');
            });

        setImagesListData((prevState) => {
            const currentImagesListWithTheseRemoved = prevState.filter(
                (currentImage: documentDefinition) => {
                    return (
                        imagesToRemove.filter((imageToRemove: documentDefinition) => {
                            return imageToRemove.documentName == currentImage.documentName;
                        }).length == 0
                    );
                }
            );
            currentImagesListWithTheseRemoved?.forEach((i) => (i.isChecked = false));
            return currentImagesListWithTheseRemoved;
        });
    };

    const setImagesListCheckedHandler = (isSelected: boolean, documentName: string) => {
        setImagesListData((prevState: documentDefinition[]) => {
            let newState = [...prevState];
            newState.forEach((element) => {
                if (element.documentName === documentName) {
                    element.isChecked = isSelected;
                }
            });
            return newState;
        });
    };

    const setImagesListCheckedAllHandler = (isSelected: boolean) => {
        setImagesListData((prevState: documentDefinition[]) => {
            let newState = [...prevState];
            newState.forEach((i) => (i.isChecked = isSelected));
            return newState;
        });
    };

    const addImagesToActivityHistoryHandler = async (imagesToAdd: documentDefinition[]) => {
        const mapped = imagesToAdd.map((image) => {
            const description = Common.generateFileName(image);

            var newImage: activityHistoryEntry = {
                name: image.name,
                fileAccessDate: image.fileAccessDate,
                displayAs: description,
            };
            return newImage;
        });

        loadingCtx.setIsLoading(
            true,
            'ImagesList addImagesToActivityHistoryHandler postActivityHistory'
        );
        await ActivityHistoryService.postActivityHistory(mapped, userName, accessToken)
            .then((response) => {
                loadingCtx.setIsLoading(
                    false,
                    'ImagesList addImagesToActivityHistoryHandler postActivityHistory'
                );
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(
                    false,
                    'ImagesList addImagesToActivityHistoryHandler postActivityHistory'
                );
            });

        loadActivityHistory();
    };

    const clearCacheHandler = () => {
        loadingCtx.setIsLoading(true, 'ImageDataProvider clearCacheHandler');

        ActivityHistoryService.deleteCache(userName, accessToken, cid)
            .then((response) => {
                loadingCtx.setIsLoading(false, 'ImageDataProvider clearCacheHandler');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ImageDataProvider clearCacheHandler');
            });

        setActivityHistoryData([]);
        setImagesListData([]);
        clearLoadedImageHandler();
    };

    const clearActivityHistoryHandler = () => {
        loadingCtx.setIsLoading(true, 'ActivityHistory clearActivityHistoryHandler');

        ActivityHistoryService.deleteActivityHistory(userName, accessToken, cid)
            .then((response) => {
                loadingCtx.setIsLoading(false, 'ActivityHistory clearActivityHistoryHandler');
            })
            .catch((ex) => {
                //setError(ex.response);
                //setIsError(true);
                loadingCtx.setIsLoading(false, 'ActivityHistory clearActivityHistoryHandler');
            });

        setActivityHistoryData([]);

        if (!loadedImageData.loadedImageFromImageList) {
            setLoadedImageData({
                loadedImageDetails: {} as documentDefinition,
                loadedImagePageNumber: 0,
                loadedImagePageUrl: '',
                loadedImageFromImageList: false,
                loadedImagePagesReady: 0,
                loadedImageTokenExpires: new Date(Date.now()),
            });
        }
    };

    const loadImageDetailsHandler = async (fileName: string) => {
        const imageDetailsFromCache = cachedImageDetails.filter((x) => x.name === fileName);
        if (imageDetailsFromCache.length > 0) {
            return;
        }

        // Go to Server for file details
        let fileDetailsResponse = undefined;
        await ImageService.getFileDetailsTooltip(fileName, accessToken, cid)
            .then((response) => {
                fileDetailsResponse = response.data as documentDefinition;
                const fileDetails: documentDefinition = fileDetailsResponse;
                setCachedImageDetails((prevState) => {
                    return [...prevState, fileDetails];
                });
            })
            .catch((ex) => {
                return;
            });
        if (!fileDetailsResponse) {
            //error
            return;
        }
        return;
    };

    const imageDataContext = {
        loadedImageDetails: loadedImageData.loadedImageDetails,
        loadedImagePageNumber: loadedImageData.loadedImagePageNumber,
        loadedImagePageUrl: loadedImageData.loadedImagePageUrl,
        loadedImageFromImageList: loadedImageData.loadedImageFromImageList,
        loadedImagePagesReady: loadedImageData.loadedImagePagesReady,
        loadedImageTokenExpires: loadedImageData.loadedImageTokenExpires,
        taggedPages: taggedPages,
        imagesList: imagesListData,
        activityHistory: activityHistoryData,
        cachedImageDetails: cachedImageDetails,
        setLoadedImage: setLoadedImageHandler,
        clearLoadedImage: clearLoadedImageHandler,
        setLoadedImagePageNumber: setLoadedImagePageNumberHandler,
        addImagesToImagesList: addImagesToImagesListHandler,
        removeImagesFromImagesList: removeImagesFromImagesListHandler,
        setImagesListChecked: setImagesListCheckedHandler,
        setImagesListCheckedAll: setImagesListCheckedAllHandler,
        addImagesToActivityHistory: addImagesToActivityHistoryHandler,
        clearActivityHistory: clearActivityHistoryHandler,
        clearCache: clearCacheHandler,
        loadImageDetails: loadImageDetailsHandler,
        addTaggedPage: addTaggedPageHandler,
        removeTaggedPage: removeTaggedPageHandler,
        clearTaggedPages: clearTaggedPagesHandler,
    };

    return (
        <ImageDataContext.Provider value={imageDataContext}>
            {props.children}
        </ImageDataContext.Provider>
    );
};

export default ImageDataProvider;
